import { getLayer, recordStyleLayer } from 'ol-mapbox-style';
import { buffer as bufferExtent } from 'ol/extent';
import { transformExtent } from 'ol/proj';
import { toFeature } from 'ol/render/Feature';
import { GeoJSON } from 'ol/format';
import { reactive, watch } from 'vue';
import booleanIntersects from '@turf/boolean-intersects';
import bufferGeometry from '@turf/buffer';
import debounce from 'debounce';
import { HIDDEN_CATEGORIES, SCHLAEGE_LAYER, SCHLAEGE_SOURCE } from '../constants';
import {
  filterStyle, map, mapReady, mapView,
} from './useMap';
import { schlagInfo } from './useSchlag';

/**
 * @typedef Topic
 * @property {string} id
 * @property {string} label
 * @property {string|undefined} warning
 * @property {string} color
 * @property {boolean} icon
 * @property {boolean} inExtent
 * @property {boolean} inSchlagExtent
 * @property {number} urlSort
 * @property {number} displaySort
 * @property {boolean} visible
 * @property {string|undefined} category
 */

const geojson = new GeoJSON();

/** @type {Array<Topic>} */
export const topics = reactive([]);

function intersects(feature, candidates) {
  return candidates.some((candidate) => booleanIntersects(feature, candidate));
}

/**
 * @param {import("ol/extent.js").Extent} extent
 * @param {boolean} [precise] Use geometry intersection instead of extent intersection
 * @returns {Promise<Array<string>>}
 */
async function findTopics(extent, precise = false) {
  const features = getLayer(map, SCHLAEGE_LAYER).getFeaturesInExtent(extent)
    .filter((feature) => feature.get('mvt:layer') !== SCHLAEGE_SOURCE || feature.get('kz_bio_oepul_jn') === 'J')
    .map((renderFeature) => toFeature(renderFeature));
  const style = await filterStyle;
  const resolution = map.getView().getResolution();
  recordStyleLayer(true);
  const topicsOfInterest = Object.keys(features.reduce((acc, cur) => {
    cur.set('mapbox-layer', undefined);
    style(cur, resolution);
    const topic = cur.get('mapbox-layer')?.metadata?.label;
    if (topic) {
      let ofInterest;
      if (precise) {
        const distance = cur.get('kz_bio_oepul_jn') === 'J' || cur.get('layer') === 'invekos_schlaege_bio_polygon' ? 100 : 10;
        const buffered = bufferGeometry(geojson.writeGeometryObject(cur.getGeometry(), { featureProjection: 'EPSG:3857' }), distance, { units: 'meters' });
        ofInterest = intersects(buffered, schlagInfo.value.parts);
      } else {
        ofInterest = cur.getGeometry().intersectsExtent(extent);
      }
      if (ofInterest) {
        acc[topic] = true;
      }
    }
    return acc;
  }, {}));
  recordStyleLayer(false);
  return topicsOfInterest;
}

async function updateTopicsInExtent() {
  const { extent } = mapView.value;
  if (extent) {
    const renderedTopics = await findTopics(transformExtent(extent, 'EPSG:4326', 'EPSG:3857'));
    topics.forEach((topic) => {
      topic.inExtent = renderedTopics.includes(topic.label);
    });
    topics.sort((a, b) => a.displaySort - b.displaySort);
  }
}

function updateTopicsInSchlagExtent() {
  if (schlagInfo.value?.extent) {
    const schlagExtent = transformExtent(schlagInfo.value.extent, 'EPSG:4326', 'EPSG:3857');
    const bufferedExtent = bufferExtent(schlagExtent, 100);
    findTopics(bufferedExtent, true).then((availableTopics) => {
      topics.forEach((topic) => {
        topic.inSchlagExtent = availableTopics.includes(topic.label);
      });
    });
  }
}

watch(schlagInfo, updateTopicsInSchlagExtent);

mapReady.then(() => {
  const { layers } = map.get('mapbox-style');
  topics.push(...Object.values(layers
    .filter((l) => l.metadata?.group === 'one' && l.type !== 'raster' && !HIDDEN_CATEGORIES?.test(l.metadata?.category))
    .map((l) => ({
      id: l.id,
      label: l.metadata?.label,
      warning: l.metadata?.warning,
      color: l.paint?.['fill-color'] || l.paint?.['icon-color'],
      icon: !!l.paint?.['icon-color'],
      urlSort: l.metadata?.urlSort,
      displaySort: l.metadata?.displaySort || 0,
      category: l.metadata?.category,
      isNew: !!l.metadata?.new,
    })).reduce((acc, {
      id, label, warning, color, icon, urlSort, displaySort, category, isNew,
    }) => {
      if (!(label in acc)) {
        acc[label] = ({
          id,
          label,
          warning,
          color,
          icon,
          inExtent: false,
          inSchlagExtent: false,
          urlSort,
          displaySort,
          category,
          visible: false,
          isNew,
        });
      }
      return acc;
    }, {})));

  map.on('postrender', debounce(updateTopicsInExtent, 200));
});

export function useTopics() {
  return { topics };
}
